import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, ArrowLeft, Lock, Sparkles } from "lucide-react";
import DocumentUpload from "@/components/DocumentUpload";

const TEMPLATES = [
  { l: "Acceptation", t: "Bonjour,\nJe vous remercie pour votre demande. Je peux intervenir sur ce sujet et vous propose de poursuivre nos échanges afin de cadrer précisément la mission.\nBien cordialement." },
  { l: "Refus poli", t: "Bonjour,\nJe vous remercie pour votre demande. Après analyse, je ne suis malheureusement pas en mesure d'intervenir utilement sur ce dossier. CONFILEX peut toutefois vous orienter vers un autre spécialiste plus adapté.\nBien cordialement." },
];

export default function Messages() {
  const { missionId } = useParams();
  const { user } = useAuth();
  const [mission, setMission] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [documents, setDocuments] = useState([]);
  const endRef = useRef(null);

  const refresh = useCallback(async () => {
    const m = await apiClient.get(`/missions/${missionId}`);
    setMission(m.data);
    // The messages endpoint returns 402 for locked spécialistes — that's expected and handled by the lock screen.
    try {
      const r = await apiClient.get(`/messages/${missionId}`);
      setMessages(r.data);
    } catch (e) {
      if (e?.response?.status && e.response.status !== 402 && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Messages fetch failed:", e.response.status);
      }
    }
    try {
      const d = await apiClient.get(`/uploads/mission/${missionId}`);
      setDocuments(d.data);
    } catch (e) {
      // 403 expected before an spécialiste is assigned to the mission; log other errors only.
      if (e?.response?.status && e.response.status !== 403 && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Mission documents fetch failed:", e.response.status);
      }
    }
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [missionId]);

  useEffect(() => { refresh(); const i = setInterval(refresh, 5000); return () => clearInterval(i); }, [refresh]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await apiClient.post("/messages", { mission_id: missionId, content: text });
      setText("");
      refresh();
    } catch (e) { toast.error("L'envoi n'a pu être effectué"); }
  };

  const unlockMission = async () => {
    try {
      const { data } = await apiClient.post(`/missions/${missionId}/unlock-checkout`, {
        origin_url: window.location.origin,
      });
      if (data.already_unlocked) { refresh(); return; }
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Le déblocage a échoué");
    }
  };

  // Spécialiste view: if mission is locked, show the unlock screen instead of the chat
  if (user?.role === "expert" && mission?.locked) {
    return (
      <div className="bg-[#F5F7FA] min-h-screen flex flex-col">
        <Navbar variant="light" />
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 w-full">
          <Link to="/expert/dashboard" className="text-sm text-slate-500 hover:text-[#0B3A2A] flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" /> Retour à l'espace spécialiste
          </Link>
          <Card className="p-8 rounded-3xl border-amber-200 bg-white" data-testid="locked-screen">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-amber-700">Dossier verrouillé</div>
                <h1 className="font-serif text-2xl text-[#0B3A2A]">{mission.title}</h1>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-6 text-sm">
              <Cell label="Prénom du contact" value={mission.client_name} />
              <Cell label="Localisation" value={mission.city || "—"} />
              <Cell label="Urgence" value={mission.urgency} />
              <Cell label="Catégorie" value={mission.category} />
            </div>
            {mission.description && (
              <div className="bg-[#FAFAF7] border border-slate-100 rounded-xl p-4 mb-6">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Synthèse anonymisée</div>
                <p className="text-sm text-slate-700 italic whitespace-pre-wrap">{mission.description}</p>
              </div>
            )}
            <p className="text-sm text-slate-600 mb-6 leading-relaxed">
              Les coordonnées complètes du client (nom, email, téléphone) ainsi que la messagerie et les pièces justificatives deviennent accessibles dès le déblocage du dossier.
            </p>
            <div className="space-y-3">
              <Button onClick={unlockMission} className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold w-full sm:w-auto" data-testid="unlock-cta">
                <Sparkles className="w-4 h-4 mr-2" />
                Débloquer ce dossier ({mission.unlock_fee_eur} €)
              </Button>
              <p className="text-xs text-slate-500">
                Alternative : un abonnement Standard, Premium ou Institutionnel actif déverrouille automatiquement l'ensemble des dossiers reçus. <Link to="/pricing" className="text-[#C9A961] hover:underline">Voir les formules</Link>.
              </p>
            </div>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#F5F7FA] min-h-screen flex flex-col">
      <Navbar variant="light" />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-8 w-full flex-1 flex flex-col">
        <Link to={user?.role === "expert" ? "/expert/dashboard" : "/dashboard"} className="text-sm text-slate-500 hover:text-[#0B3A2A] flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        {mission && (
          <Card className="p-5 mb-4 rounded-2xl border-slate-100">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h1 className="font-serif text-xl text-[#0B3A2A]">{mission.title}</h1>
                <p className="text-sm text-slate-500">{mission.category} · {mission.urgency}</p>
              </div>
              <Badge className="bg-[#C9A961]/10 text-[#C9A961] border border-[#C9A961]/30 rounded-full capitalize">{mission.status}</Badge>
            </div>
          </Card>
        )}

        {/* Client-owned mission: control access granted to spécialistes */}
        {mission && user?.id === mission.user_id && (
          <ClientAccessPanel missionId={missionId} />
        )}

        {/* Mission documents — owner can add/remove, spécialiste can view/download */}
        {mission && (
          <Card className="p-5 mb-4 rounded-2xl border-slate-100">
            <DocumentUpload
              missionId={missionId}
              initialDocs={documents}
              onDocsChange={setDocuments}
              readOnly={user?.role === "expert" || (user?.role === "client" && mission.user_id !== user?.id)}
              title="Pièces du dossier"
              description={
                user?.role === "expert"
                  ? "Documents confidentiels transmis par le client. Téléchargement réservé au spécialiste assigné."
                  : "Documents joints au dossier. Ils sont accessibles au spécialiste assigné une fois la mise en relation engagée."
              }
            />
          </Card>
        )}

        <Card className="flex-1 flex flex-col rounded-2xl border-slate-100 overflow-hidden min-h-[400px]">
          <div className="flex-1 p-6 overflow-y-auto space-y-3" data-testid="messages-list">
            {messages.length === 0 && <p className="text-center text-sm text-slate-400 py-10">Aucun échange. Initiez la conversation ci-dessous.</p>}
            {messages.map(m => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${mine ? "bg-[#0B3A2A] text-white" : "bg-slate-100 text-[#0B3A2A]"}`}>
                    <div className="text-xs opacity-70 mb-1">{m.sender_name} · {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="whitespace-pre-wrap text-sm">{m.content}</div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef}></div>
          </div>
          <div className="border-t border-slate-100 p-4">
            <div className="flex gap-2 mb-2 flex-wrap">
              {TEMPLATES.map(t => (
                <Button key={t.l} type="button" variant="outline" size="sm" onClick={() => setText(t.t)} className="rounded-full text-xs">
                  {t.l}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Votre message…" data-testid="message-input"
                className="rounded-xl border-slate-200 resize-none flex-1" />
              <Button onClick={send} data-testid="send-message"
                className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold self-end h-11 px-6">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-[#0B3A2A] font-medium mt-0.5">{value}</div>
    </div>
  );
}

function ClientAccessPanel({ missionId }) {
  const [unlocks, setUnlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/missions/${missionId}/access`);
      setUnlocks(data);
    } finally { setLoading(false); }
  }, [missionId]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (expertId) => {
    try {
      await apiClient.post(`/missions/${missionId}/revoke-access`, { expert_id: expertId });
      toast.success("Accès retiré");
      load();
    } catch { toast.error("Le retrait a échoué"); }
  };

  return (
    <Card className="p-5 mb-4 rounded-2xl border-slate-100" data-testid="client-access-panel">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-4 h-4 text-[#C9A961]" />
        <h3 className="font-serif text-base text-[#0B3A2A]">Accès accordés à vos coordonnées</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4 leading-relaxed">
        Vos coordonnées complètes restent masquées par défaut. Seuls les spécialistes ci-dessous peuvent vous contacter directement.
      </p>
      {loading ? (
        <div className="text-sm text-slate-400">Chargement…</div>
      ) : unlocks.length === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-2.5">
          Aucun spécialiste n'a actuellement accès à vos coordonnées complètes.
        </div>
      ) : (
        <div className="space-y-2">
          {unlocks.map(u => (
            <div key={u.id} className="flex items-center justify-between gap-3 bg-white border border-slate-100 rounded-lg px-3 py-2" data-testid={`access-row-${u.expert_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#0B3A2A]">
                  {u.expert?.first_name} {u.expert?.last_name}
                  <span className="text-xs text-slate-400 ml-2">· {u.expert?.profession}</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">
                  {u.method === "consent" ? "Consentement explicite" :
                   u.method === "paid" ? "Accès payé par le spécialiste" :
                   u.method === "subscription" ? "Abonnement actif" : u.method}
                </div>
              </div>
              {u.method === "consent" && (
                <Button size="sm" variant="ghost" className="rounded-full text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  onClick={() => revoke(u.expert_id)}
                  data-testid={`revoke-${u.expert_id}`}>
                  Retirer
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
